import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { useAccount } from '../../context/AccountContext';
import { GET_TINYCMS_SITE_HEALTH } from '../../graphql/queries';
import { ACT_ON_TINYCMS_SITE_HEALTH, RUN_TINYCMS_SITE_HEALTH } from '../../graphql/mutations';
import { track } from '../../lib/usage';
import { useArticleAddressRepairs } from './ArticleAddressRepairs';
import {
  AREAS,
  areaDescription,
  areaName,
  checkedNote,
  doneNote,
  healthHeadline,
  healthSubLine,
  historyDate,
  itemLink,
  speedExtra,
  STATUS_WORD,
  SUGGESTIONS_SHOWN,
  todayHealth,
  visibleSuggestions,
  type HealthArea,
  type HealthStatus,
  type HealthSuggestion,
  type SiteHealthView,
  withAddressItem,
} from './healthWords';

/**
 * Site health: Today's panel and Website › Manage › Site health (designer's notes
 * "Site Health (P2, P5, P6, P7)", app/Site Health Concept). One status per site, a
 * word per area, and what to do next. The checks behind them stay on the server.
 */

export const HEALTH_PAGE = '/content?view=manage&area=health';
const POLL_WHILE_CHECKING_MS = 20000;

const HEADLINE_DOT = (view: SiteHealthView): HealthStatus | 'none' =>
  view.state === 'ready' ? view.overall || 'good' : 'none';

function useSiteHealth() {
  const { accountId } = useAccount();
  const q = useQuery<{ tinycmsSiteHealth: SiteHealthView }>(GET_TINYCMS_SITE_HEALTH, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const checked = q.data?.tinycmsSiteHealth || null;
  const addressesToFix = useArticleAddressRepairs()?.total || 0;
  const view = useMemo(
    () => (checked ? withAddressItem(checked, addressesToFix) : null),
    [checked, addressesToFix]
  );
  const { startPolling, stopPolling } = q;
  const checking = Boolean(view?.checking);
  useEffect(() => {
    if (checking) startPolling(POLL_WHILE_CHECKING_MS);
    else stopPolling();
    return () => stopPolling();
  }, [checking, startPolling, stopPolling]);
  return { view, loading: q.loading && !view, refetch: q.refetch, accountId };
}

const Dot: React.FC<{ status: HealthStatus | 'none'; size?: number }> = ({ status, size = 8 }) => (
  <span className="sh-dot" data-status={status} style={{ width: size, height: size }} aria-hidden />
);

// ── Today ────────────────────────────────────────────────────────────────────

/**
 * A cell of Today's Performance grid, on every plan (app/Today Site Health Column).
 * Three columns on a wide screen, a full-width band above Website and Social when
 * they don't fit, and first in one column on a phone (ink.css .sh-today).
 */
export const TodaySiteHealth: React.FC = () => {
  const { view, refetch, accountId } = useSiteHealth();
  const [run, { loading: starting }] = useMutation(RUN_TINYCMS_SITE_HEALTH);
  const [error, setError] = useState('');
  const h = view ? todayHealth(view) : null;
  const checkNow = async () => {
    setError('');
    track('health_check_now', {});
    try {
      await run({ variables: { accountId } });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That didn’t work. Try again.');
    }
    await refetch();
  };
  const areaChip = (a: (typeof AREAS)[number]) => {
    const status = view?.areas?.[a.key] || 'good';
    return h?.hollow ? (
      <span key={a.key} className="sh-tchip" title="Not checked yet" data-hollow="true">
        <Dot status="none" size={7} />
        {a.short}
      </span>
    ) : (
      <Link
        key={a.key}
        to={`${HEALTH_PAGE}&healthArea=${a.key}`}
        className="sh-tchip"
        title={`${a.short}: ${STATUS_WORD[status]}`}
      >
        <Dot status={status} size={7} />
        {a.short}
      </Link>
    );
  };
  const rowTo = (id: string) => `${HEALTH_PAGE}&suggestion=${encodeURIComponent(id)}`;
  const short = (key: HealthArea) => AREAS.find((a) => a.key === key)?.short || key;
  const top = h?.rows[0];
  return (
    <div className="today-perf-cell sh-today">
      <div className="sh-today-main">
        <div className="today-perf-head">
          <span className="today-perf-name">Site health</span>
          {h ? <span className="today-perf-where">{h.when}</span> : null}
          <Link to={HEALTH_PAGE} className="sh-open">
            Open <span aria-hidden>→</span>
          </Link>
        </div>
        {h ? (
          <>
            <Link to={HEALTH_PAGE} className="sh-tap">
              <div className="sh-head">
                <span
                  className="sh-dot"
                  data-status={h.dot}
                  data-pulse={h.pulse ? 'true' : undefined}
                  style={{ width: 10, height: 10 }}
                  aria-hidden
                />
                <div className="sh-headline">{h.headline}</div>
              </div>
              <div className="sh-sub">{h.sub}</div>
            </Link>
            {h.action ? (
              <div className="sh-today-act">
                <button
                  type="button"
                  className="ink-btn"
                  disabled={starting}
                  onClick={() => void checkNow()}
                >
                  {h.action === 'retry' ? 'Try again' : 'Check now'}
                </button>
              </div>
            ) : null}
            {error ? <div className="sh-today-link ink-field-error">{error}</div> : null}
            {h.link ? (
              <Link to={h.link.to} className="sh-today-link">
                {h.link.label}
              </Link>
            ) : null}
          </>
        ) : null}
      </div>
      {h ? (
        <div className="sh-today-side" data-dim={h.dim ? 'true' : undefined}>
          <div className="sh-tchips">{AREAS.map(areaChip)}</div>
          {/* Wide: the top three. Narrow: the worst, and how many more. */}
          {h.rows.length ? (
            <div className="sh-next">
              <div className="sh-next-label">What to do next</div>
              {h.rows.map((s) => (
                <Link key={s.id} to={rowTo(s.id)} className="sh-next-row">
                  <Dot status={s.severity} size={7} />
                  <span className="sh-next-text">{s.title}</span>
                  <span className="sh-next-area">{short(s.area)}</span>
                </Link>
              ))}
              {h.open.length > h.rows.length ? (
                <Link to={HEALTH_PAGE} className="sh-next-more">
                  + {h.open.length - h.rows.length} more
                </Link>
              ) : null}
            </div>
          ) : null}
          {top ? (
            <div className="sh-top">
              <Link to={rowTo(top.id)} className="sh-top-row">
                <Dot status={top.severity} size={7} />
                <span className="sh-next-text">{top.title}</span>
                <span className="sh-top-go" aria-hidden>
                  ›
                </span>
              </Link>
              {h.open.length > 1 ? (
                <Link to={HEALTH_PAGE} className="sh-top-more">
                  + {h.open.length - 1} more
                </Link>
              ) : null}
            </div>
          ) : null}
          {h.fixed ? (
            <div className="sh-fixed">
              <strong>
                {h.fixed} {h.fixed === 1 ? 'thing' : 'things'}
              </strong>{' '}
              fixed by your agent before they reached you this month.
            </div>
          ) : null}
          <Link to={HEALTH_PAGE} className="sh-band-open">
            <span>
              <span className="sh-band-open-wide">Open Site health</span>
              <span className="sh-band-open-phone">
                {h.open.length ? `See all ${h.open.length} in Site health` : 'Open Site health'}
              </span>
            </span>
            <span aria-hidden> →</span>
          </Link>
        </div>
      ) : null}
    </div>
  );
};

// ── A suggestion ─────────────────────────────────────────────────────────────

const Chips: React.FC<{ s: HealthSuggestion }> = ({ s }) => {
  if (!s.items.length) return null;
  if (s.itemsLabel) {
    return (
      <div className="sh-chips">
        <span className="sh-chip">{s.itemsLabel}</span>
      </div>
    );
  }
  return (
    <div className="sh-chips">
      {s.items.map((i) => (
        <Link
          key={`${i.kind}:${i.id}`}
          to={itemLink(i)}
          className="sh-chip"
          onClick={(e) => e.stopPropagation()}
        >
          {i.title}
        </Link>
      ))}
    </div>
  );
};

const SuggestionCard: React.FC<{
  s: HealthSuggestion;
  open: boolean;
  canKeep: boolean;
  busy: boolean;
  error: string;
  onToggle: () => void;
  onAct: (action: 'agent' | 'review' | 'keep', reason?: string) => void;
}> = ({ s, open, canKeep, busy, error, onToggle, onAct }) => {
  const [keeping, setKeeping] = useState(false);
  const [reason, setReason] = useState('');
  const done = s.done;
  const isOpen = open && !done;
  const canBeKept = canKeep && s.action === 'keep+agent';
  return (
    <div className="sh-card" data-done={done ? 'true' : undefined}>
      <button
        type="button"
        className="sh-card-head"
        onClick={onToggle}
        disabled={Boolean(done)}
        aria-expanded={isOpen}
      >
        <Dot status={done ? 'none' : s.severity} />
        <span className="sh-card-text">
          <span className="sh-card-area">{areaName(s.area)}</span>
          <span className="sh-card-title">{s.title}</span>
        </span>
        {done ? null : (
          <span className="sh-card-chev" aria-hidden>
            {isOpen ? '−' : '+'}
          </span>
        )}
      </button>
      <div className="sh-card-chips">
        <Chips s={s} />
      </div>
      {done ? <div className="sh-card-done">{doneNote(done)}</div> : null}
      {isOpen ? (
        <div className="sh-card-body">
          <p className="sh-card-why">{s.why}</p>
          {s.agentPrompt ? (
            <div className="sh-prompt">
              <div className="sh-prompt-label">What your agent gets</div>
              <div className="sh-prompt-text">{s.agentPrompt}</div>
            </div>
          ) : null}
          <div className="sh-card-actions">
            {s.to ? (
              <Link to={s.to} className="ink-btn ink-btn-primary">
                {s.cta}
              </Link>
            ) : (
              <button
                type="button"
                className="ink-btn ink-btn-primary"
                disabled={busy}
                onClick={() => onAct(s.action === 'review' ? 'review' : 'agent')}
              >
                {s.cta}
              </button>
            )}
            {canBeKept ? (
              <button type="button" className="ink-link-btn" onClick={() => setKeeping((v) => !v)}>
                I added this on purpose
              </button>
            ) : null}
          </div>
          {keeping && canBeKept ? (
            <div className="sh-keep">
              <input
                className="ink-input"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="What does it do? (saved with your name)"
                aria-label="What does it do?"
              />
              <button
                type="button"
                className="ink-btn"
                disabled={busy || !reason.trim()}
                onClick={() => onAct('keep', reason.trim())}
              >
                Keep it
              </button>
            </div>
          ) : null}
          {error ? (
            <p className="sh-error" role="alert">
              {error}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

// ── Website › Manage › Site health ───────────────────────────────────────────

const AreaRow: React.FC<{
  area: (typeof AREAS)[number];
  view: SiteHealthView;
  active: boolean;
  onPick: () => void;
}> = ({ area, view, active, onPick }) => {
  const status = view.areas?.[area.key] || 'good';
  const extra = area.key === 'speed' ? speedExtra(view) : null;
  return (
    <button
      type="button"
      className="sh-area"
      data-active={active ? 'true' : undefined}
      aria-pressed={active}
      onClick={onPick}
    >
      <span className="sh-area-text">
        <span className="sh-area-name">
          {area.name}
          {area.isNew ? <span className="sh-new">New</span> : null}
        </span>
        <span className="sh-area-desc">{areaDescription(area.key, view)}</span>
        {extra ? <span className="sh-area-extra">{extra}</span> : null}
      </span>
      <span className="sh-area-status" data-status={status}>
        <Dot status={status} />
        {STATUS_WORD[status]}
      </span>
    </button>
  );
};

const History: React.FC<{ rows: NonNullable<SiteHealthView['history']>; onBack: () => void }> = ({
  rows,
  onBack,
}) => (
  <div className="sh-history">
    <button type="button" className="ink-link-btn" onClick={onBack}>
      ← Back to Site health
    </button>
    <h3 className="sh-section-title">Last 90 days</h3>
    {rows.length ? (
      <table className="sh-history-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Overall</th>
            {AREAS.map((a) => (
              <th key={a.key}>{a.short}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.runAt}>
              <td>{historyDate(r.runAt)}</td>
              <td>
                <span className="sh-area-status" data-status={r.overall}>
                  <Dot status={r.overall} />
                  {STATUS_WORD[r.overall]}
                </span>
              </td>
              {AREAS.map((a) => (
                <td key={a.key}>
                  <Dot status={r.areas?.[a.key] || 'good'} size={7} />
                  <span className="sr-only">{STATUS_WORD[r.areas?.[a.key] || 'good']}</span>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    ) : (
      <p className="sh-note">Nothing checked yet.</p>
    )}
  </div>
);

export const ManageSiteHealth: React.FC = () => {
  const { view, loading, refetch, accountId } = useSiteHealth();
  // Today's chips and rows open the page at what was clicked.
  const [params] = useSearchParams();
  const [area, setArea] = useState<HealthArea | null>(
    () => (params.get('healthArea') as HealthArea | null) || null
  );
  const [openId, setOpenId] = useState<string | null>(() => params.get('suggestion'));
  const [showAll, setShowAll] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [error, setError] = useState('');
  const [act, { loading: acting }] = useMutation(ACT_ON_TINYCMS_SITE_HEALTH);
  const [run, { loading: starting }] = useMutation(RUN_TINYCMS_SITE_HEALTH);

  useEffect(() => {
    if (view) track('health_viewed', { area: view.state });
    // Once per visit to the page, whatever refetches later.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Boolean(view)]);

  const onAct = async (
    s: HealthSuggestion,
    action: 'agent' | 'review' | 'keep',
    reason?: string
  ) => {
    setError('');
    track(
      action === 'agent'
        ? 'suggestion_ask_agent'
        : action === 'keep'
          ? 'suggestion_kept'
          : 'suggestion_reviewed',
      { area: s.area }
    );
    try {
      await act({ variables: { accountId, suggestionId: s.id, action, reason } });
      setOpenId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That didn’t work. Try again.');
    }
    await refetch();
  };

  const checkNow = async () => {
    setError('');
    track('health_check_now', {});
    try {
      await run({ variables: { accountId } });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That didn’t work. Try again.');
    }
    await refetch();
  };

  const head = (
    <div className="mg-head">
      <h2 className="mg-title">Site health</h2>
      <p className="mg-sub">
        How your site is doing for visitors, search engines and AI assistants. We check every night.
      </p>
    </div>
  );

  if (loading || !view) return head;

  if (showHistory && view.history) {
    return (
      <>
        {head}
        <History rows={view.history} onBack={() => setShowHistory(false)} />
      </>
    );
  }

  const suggestions = view.suggestions || [];
  const list = visibleSuggestions(suggestions, area);
  // Acted-on cards stay in view with their note: only open ones count toward the first few.
  const openCards = list.filter((s) => !s.done);
  const shown = showAll
    ? list
    : [...openCards.slice(0, SUGGESTIONS_SHOWN), ...list.filter((s) => s.done)];
  const hiddenCards = list.length - shown.length;
  const note = checkedNote(view);
  const fixed = view.fixedBeforeReview30d || 0;

  return (
    <>
      {head}
      <div className="sh-status">
        <Dot status={HEADLINE_DOT(view)} size={10} />
        <div className="sh-status-text">
          <div className="sh-status-head">{healthHeadline(view)}</div>
          <div className="sh-sub">
            {healthSubLine(view)}
            {note ? ` · ${note}` : ''}
          </div>
        </div>
        {view.canCheckNow ? (
          <button
            type="button"
            className="ink-btn"
            disabled={starting}
            onClick={() => void checkNow()}
          >
            Check now
          </button>
        ) : null}
      </div>
      {view.state === 'ready' ? (
        <>
          <div className="sh-areas">
            {AREAS.map((a) => (
              <AreaRow
                key={a.key}
                area={a}
                view={view}
                active={area === a.key}
                onPick={() => {
                  const next = area === a.key ? null : a.key;
                  setArea(next);
                  if (next) track('health_area_filtered', { area: next });
                }}
              />
            ))}
          </div>
          <div className="sh-next-head">
            <h3 className="sh-section-title">What to do next</h3>
            <span className="sh-note">
              {area
                ? `Only ${areaName(area)}`
                : suggestions.length
                  ? 'Most important first. Click an area to filter.'
                  : ''}
            </span>
            {area ? (
              <button
                type="button"
                className="ink-link-btn sh-show-all"
                onClick={() => setArea(null)}
              >
                Show all
              </button>
            ) : null}
          </div>
          <div className="sh-cards">
            {shown.map((s) => (
              <SuggestionCard
                key={s.id}
                s={s}
                open={openId === s.id}
                canKeep={view.canKeep}
                busy={acting}
                error={openId === s.id ? error : ''}
                onToggle={() => {
                  const next = openId === s.id ? null : s.id;
                  setOpenId(next);
                  setError('');
                  if (next) track('suggestion_opened', { area: s.area });
                }}
                onAct={(action, reason) => void onAct(s, action, reason)}
              />
            ))}
            {!list.length ? (
              <p className="sh-note">{area ? 'Nothing to do here.' : 'Nothing to do.'}</p>
            ) : null}
            {hiddenCards > 0 ? (
              <button type="button" className="ink-link-btn" onClick={() => setShowAll(true)}>
                Show {hiddenCards} more
              </button>
            ) : null}
          </div>
          <div className="sh-footer">
            <span>
              <strong>
                {fixed} {fixed === 1 ? 'thing' : 'things'}
              </strong>{' '}
              fixed by your agent before {fixed === 1 ? 'it' : 'they'} reached you this month.
            </span>
            {view.features.history ? (
              <button
                type="button"
                className="ink-link-btn sh-show-all"
                onClick={() => setShowHistory(true)}
              >
                Last 90 days <span aria-hidden>→</span>
              </button>
            ) : null}
          </div>
        </>
      ) : null}
      {error && !openId ? (
        <p className="sh-error" role="alert">
          {error}
        </p>
      ) : null}
    </>
  );
};
